import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CostBuilderDemoStep } from '../../models/cost-builder-demo.model';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-cost-builder-demo',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cost-builder-demo.component.html',
  styleUrls: ['./cost-builder-demo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CostBuilderDemoComponent implements OnChanges {
  @Input() steps: CostBuilderDemoStep[] = [];
  @Input() startIndex = 0;
  @Input() rfqName?: string;

  @Output() close = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();
  @Output() stepChange = new EventEmitter<number>();

  currentIndex = 0;
  private readonly sanitizedSources = new Map<string, SafeResourceUrl>();

  constructor(private readonly sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startIndex'] && typeof changes['startIndex'].currentValue === 'number') {
      this.setCurrentIndex(changes['startIndex'].currentValue);
    }

    if (changes['steps'] && Array.isArray(changes['steps'].currentValue) && this.steps.length > 0) {
      this.setCurrentIndex(this.startIndex);
    }
  }

  get totalSteps(): number {
    return this.steps.length;
  }

  get currentStep(): CostBuilderDemoStep | undefined {
    if (this.steps.length === 0) {
      return undefined;
    }
    return this.steps[this.currentIndex];
  }

  get canGoBack(): boolean {
    return this.currentIndex > 0;
  }

  get isLastStep(): boolean {
    return this.currentIndex === this.totalSteps - 1;
  }

  get primaryActionLabel(): string {
    return this.isLastStep ? 'Done' : 'Next';
  }

  getMediaUrl(step: CostBuilderDemoStep): SafeResourceUrl {
    const { source } = step.media;
    const fullUrl = this.getFullS3Url(source);

    if (this.sanitizedSources.has(fullUrl)) {
      return this.sanitizedSources.get(fullUrl)!;
    }

    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
    this.sanitizedSources.set(fullUrl, safeUrl);
    return safeUrl;
  }

  getFullS3Url(relativePath: string): string {
    const baseUrl = environment.s3BaseUrl || '';
    const normalizedPath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    return `${baseUrl}/${normalizedPath}`;
  }

  goToPrevious(): void {
    if (!this.canGoBack) {
      return;
    }

    this.setCurrentIndex(this.currentIndex - 1);
  }

  onPrimaryAction(): void {
    if (this.isLastStep) {
      this.completed.emit();
      return;
    }

    this.setCurrentIndex(this.currentIndex + 1);
  }

  onClose(): void {
    this.close.emit();
  }

  private setCurrentIndex(index: number): void {
    if (this.steps.length === 0) {
      this.currentIndex = 0;
      return;
    }

    const clampedIndex = Math.max(0, Math.min(index, this.totalSteps - 1));
    this.currentIndex = clampedIndex;
    this.stepChange.emit(this.currentIndex);
  }
}

